import { FC, useMemo, useState, useRef, useEffect } from 'react';
import { StepViewModel } from '../../models/waterfall';
import { IntegrationSettings } from '../../models/integrationSettings';
import styles from './StepNode.module.css';
import { StepNodeActionButton } from './StepNodeActionButton';
import { isStepValid } from './stepUtils';

interface StepNodeProps {
  step: StepViewModel;
  level?: number;
  isLastChild?: boolean;
  isFirstChild?: boolean;
  siblingCount?: number;
  parentHasSiblings?: boolean[];
  onStepClick: (step: StepViewModel, e?: React.MouseEvent) => void;
  onAddStepClick: (parentStep: StepViewModel, e: React.MouseEvent) => void;
  onDeleteStepClick?: (step: StepViewModel, e: React.MouseEvent) => void;
  onReorderStepClick?: (step: StepViewModel, direction: 'up' | 'down', e: React.MouseEvent) => void;
  isRootDeletable?: boolean;
  allStepsValid?: boolean;
  integrationSettings?: IntegrationSettings[];
}

const HIDE_DELAY_MS = 300;

function formatMinutes(totalMinutes: number): string {
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const mins = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
  return parts.join(' ');
}

export const StepNode: FC<StepNodeProps> = ({
  step,
  level = 0,
  isLastChild = false,
  isFirstChild = true,
  siblingCount = 1,
  parentHasSiblings = [],
  onStepClick,
  onAddStepClick,
  onDeleteStepClick,
  onReorderStepClick,
  isRootDeletable = false,
  allStepsValid = true,
  integrationSettings = [],
}) => {
  const hasNextSteps = step.nextSteps && step.nextSteps.length > 0;
  const canMoveUp = siblingCount > 1 && !isFirstChild;
  const canMoveDown = siblingCount > 1 && !isLastChild;
  const [showButtons, setShowButtons] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isCurrentStepValid = useMemo(() => {
    return isStepValid(step);
  }, [step]);

  // Get vendor name(s) from integration settings
  const vendorName = useMemo(() => {
    if (!step.integrationSettingsIds || step.integrationSettingsIds.length === 0) return null;
    const names = step.integrationSettingsIds
      .map(id => integrationSettings.find(s => s.integrationSettingsId === id)?.name)
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : null;
  }, [step.integrationSettingsIds, integrationSettings]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setShowButtons(true);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowButtons(false);
    }, HIDE_DELAY_MS);
  };

  const paddingClass = level === 0 ? 'pb-2' : 'py-2';

  const connectionDivs = useMemo(() => {
    return Array.from({ length: level }, (_, index) => {
      const isRightmost = index === level - 1;
      const classes = [styles.connectionLine];


      if (isRightmost) {
        classes.push(styles.connectionLineWithHorizontal);
      }


      if (isRightmost && level > 0) {
        classes.push(isLastChild ? styles.connectionLineHalfVertical : styles.connectionLineFullVertical);
      } else if (parentHasSiblings[index]) {
        classes.push(styles.connectionLineFullVertical);
      }

      return <div key={`connection-${index}`} className={classes.join(' ')} style={{ width: '3rem' }} />;
    });
  }, [level, isLastChild, parentHasSiblings]);

  return (
    <div>
      <div className={`flex items-center ${paddingClass}`}>
        {/* Connection line divs for indentation */}
        {connectionDivs}

        {/* Card and buttons wrapper */}
        <div className="flex items-center gap-2" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {/* Step card */}
          <div
            className={`rounded-lg py-2 px-[18px] bg-white cursor-pointer shadow-sm ${styles.stepCard} ${hasNextSteps ? styles.nodeWithChildren : ''} ${
              isCurrentStepValid ? 'border-2 border-solid border-blue-400' : 'border-2 border-dashed border-gray-400'
            }`}
            onClick={(e) => onStepClick(step, e)}
          >
            {/* Vendor name */}
            <div className="break-words text-sm font-bold mb-1">
              {vendorName || 'No Vendor Selected'}
            </div>
            
            {/* Step details */}
            <div className="flex flex-col gap-1 text-xs text-gray-600">
              {/* Description (only show if not blank) */}
              {step.description && step.description.trim() && (
                <div>
                  <span>{step.description}</span>
                </div>
              )}
              
              {step.stepTimeoutInMinutes && (
                <div className="flex items-center gap-1">
                  <i className="pi pi-clock" style={{ fontSize: '0.7rem' }} />
                  <span>{formatMinutes(step.stepTimeoutInMinutes)} timeout</span>
                </div>
              )}
              
              {step.onTimeoutExceededAction && (
                <div className="flex items-center gap-1">
                  <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.7rem' }} />
                  <span>On Timeout: {step.onTimeoutExceededAction}</span>
                </div>
              )}
              
              {step.onFatalErrorAction && (
                <div className="flex items-center gap-1">
                  <i className="pi pi-times-circle" style={{ fontSize: '0.7rem' }} />
                  <span>On Error: {step.onFatalErrorAction}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div
            className={`flex items-center gap-2 transition-opacity duration-200 ${showButtons ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            {siblingCount > 1 && onReorderStepClick && (
              <div className="flex flex-col gap-1">
                <StepNodeActionButton onClick={(e) => onReorderStepClick(step, 'up', e)} title="Move step up" variant="move-up" disabled={!canMoveUp} />
                <StepNodeActionButton onClick={(e) => onReorderStepClick(step, 'down', e)} title="Move step down" variant="move-down" disabled={!canMoveDown} />
              </div>
            )}

            <StepNodeActionButton
              onClick={(e) => onAddStepClick(step, e)}
              title={allStepsValid ? "Add next step" : "Complete all step details before adding more steps"}
              variant="add"
              disabled={!allStepsValid}
            />

            {onDeleteStepClick && (level > 0 || isRootDeletable) && (
              <StepNodeActionButton onClick={(e) => onDeleteStepClick(step, e)} title="Delete step" variant="delete" />
            )}
          </div>
        </div>
      </div>


      {hasNextSteps && (
        <div>
          {step.nextSteps!.map((nextStep, index, array) => {
            const isLast = index === array.length - 1;
            const isFirst = index === 0;
            /**
             * Build sibling tracking array for children:
             * - Root (level 0): Pass empty array (no parent columns to track)
             * - Other levels: Append whether current node has siblings (!isLastChild)
             * This tells descendants which ancestor columns need vertical lines
             */
            const childParentHasSiblings = level > 0 ? [...parentHasSiblings, !isLastChild] : parentHasSiblings;

            return (
              <StepNode
                key={nextStep.stepGuid}
                step={nextStep}
                level={level + 1}
                isLastChild={isLast}
                isFirstChild={isFirst}
                siblingCount={array.length}
                parentHasSiblings={childParentHasSiblings}
                onStepClick={onStepClick}
                onAddStepClick={onAddStepClick}
                onDeleteStepClick={onDeleteStepClick}
                onReorderStepClick={onReorderStepClick}
                allStepsValid={allStepsValid}
                integrationSettings={integrationSettings}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
